import { useCallback, useRef } from 'react'

import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router'
import { useSearchParams } from 'react-router-dom'

import { ChatType } from '@tribeplatform/gql-client/types'
import { useAuthMember, useChat } from '@tribeplatform/react-sdk/hooks'
import { IconButton } from '@tribeplatform/react-ui-kit/Button'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { VirtualListImperativeRef } from '@tribeplatform/react-ui-kit/VirtualList'

import { useI18n } from '../../i18n/providers/I18nProvider.js'
import { useSocket } from '../../Providers/Socket/SocketProvider.js'
import { useChatContext } from '../ChatContext/ChatContext.js'
import { ChatHeader } from '../ChatHeader.js'
import {
  emitIsTyping,
  emitStoppedTyping,
} from '../emitters/is-typing-emitter.js'
import { GroupChatHeader } from '../GroupChatHeader.js'
import { ComposerSubmitParams } from '../types.js'
import { getChatDetails } from '../utils.js'

import { ChatComposerContainer } from './ChatComposerContainer.js'
import { ChatComposerWrapper } from './ChatComposerWrapper.js'
import { ChatMessages } from './ChatMessages.js'
import { ChatNotFound } from './ChatNotFound.js'
import { isChatToMyself } from './utils/chat.utils.js'

type ChatContainerProps = {
  onShowDetails?: () => void
}

export const ChatContainer = ({ onShowDetails: _ }: ChatContainerProps) => {
  const intl = useI18n()
  const { $t } = intl
  const messagesRef = useRef<VirtualListImperativeRef>(null)
  const navigate = useNavigate()
  const { data: authMember } = useAuthMember()
  const { chatId, sendMessageToExistingChat, isEditingMessage } =
    useChatContext()
  const {
    data: chat,
    error,
    isLoading,
  } = useChat({ variables: { id: chatId } })
  const { socket } = useSocket()
  const [searchParams] = useSearchParams()

  const handleComposerKeyDown = useCallback(() => {
    if (!isEditingMessage) {
      emitIsTyping(chatId, socket)
    }
  }, [socket, chatId, isEditingMessage])

  const handleComposerSubmit = useCallback(
    async ({ isSendingGiphy, giphyFiles }: ComposerSubmitParams) => {
      if (!chat?.currentParticipant?._id) {
        return
      }

      await sendMessageToExistingChat({
        authorParticipantId: chat.currentParticipant._id,
        isSendingGiphy,
        giphyFiles,
      })

      if (!isEditingMessage) {
        messagesRef.current?.goToBottom()
      }

      emitStoppedTyping(chatId, socket)
    },
    [
      chat?.currentParticipant._id,
      isEditingMessage,
      chatId,
      socket,
      sendMessageToExistingChat,
    ],
  )

  if (isLoading) {
    return (
      <div className="h-full w-full flex flex-col items-center justify-center p-4">
        <div className="h-5 bg-skeleton rounded-base w-32 mb-2 animate-pulse"></div>
        <div className="h-5 bg-skeleton rounded-base w-32 mb-2 animate-pulse"></div>
        <div className="h-4 bg-skeleton rounded-base w-64 animate-pulse"></div>
      </div>
    )
  }

  if (!chat && !!error) {
    return <ChatNotFound />
  }

  const defaultTitle = $t({
    id: 'Generics.Messages',
    defaultMessage: 'Messages',
  })

  const chatProps = getChatDetails({ chat, authMember, intl })
  const { name: chatName, imageUrl, type } = chatProps
  const isSavedChat = isChatToMyself(chat)

  return (
    <>
      <Helmet>
        <title>
          {chatName ? `${defaultTitle} - ${chatName}` : defaultTitle}
        </title>
      </Helmet>
      <div className="flex flex-col h-full">
        <div className="shrink-0 flex items-center border-b border-line-subdued">
          <div className="flex items-center sm:hidden me-2">
            <IconButton
              variant="plainNeutral"
              size="lg"
              rounded
              onClick={() => navigate(`/messaging?${searchParams.toString()}`)}
              icon={<SvgIcon className="rtl:-scale-x-100" name="arrow-left" />}
            />
          </div>
          <div className="flex grow min-w-0 p-3 items-center">
            {type === ChatType.Group ? (
              <GroupChatHeader
                chatId={chatId}
                name={chatName}
                imageUrl={imageUrl}
              />
            ) : (
              <ChatHeader
                chatId={chatId}
                name={chatName}
                imageUrl={imageUrl}
                isChatToMyself={isSavedChat}
              />
            )}
          </div>
        </div>
        <div className="grow relative min-h-[8rem]">
          <div className="absolute inset-0">
            <ChatMessages key={chatId} chatId={chat._id} ref={messagesRef} />
          </div>
        </div>
        <ChatComposerWrapper chat={chat}>
          <ChatComposerContainer
            disabled={false}
            onComposerSubmit={handleComposerSubmit}
            onKeyDown={handleComposerKeyDown}
          />
        </ChatComposerWrapper>
      </div>
    </>
  )
}
